import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Mail, Clock, User, MessageSquare, Trash2, CheckCircle, RefreshCw, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: getAuthHeaders()
      });
      setMessages(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-[#0096FF]/20 text-[#0096FF] border-[#0096FF]/30';
      case 'read':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'replied':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-[#121212] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[7.6923%] py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                variant="ghost"
                className="text-white/70 hover:text-red-400 hover:bg-red-500/10 p-2 gap-2"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden md:inline">Logout</span>
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-white">Admin Panel</h1>
                <p className="text-white/50 text-sm">Manage Contact Messages</p>
              </div>
            </div>
            <Button
              onClick={fetchMessages}
              disabled={loading}
              className="bg-[#0096FF] text-black hover:bg-[rgba(0,150,255,0.1)] hover:text-[#0096FF] font-medium px-4 py-2 rounded-none transition-all duration-400 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-[7.6923%] py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0096FF]/10">
                <Mail className="w-6 h-6 text-[#0096FF]" />
              </div>
              <div>
                <p className="text-3xl font-semibold text-white">{messages.length}</p>
                <p className="text-white/50 text-sm">Total Messages</p>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0096FF]/10">
                <MessageSquare className="w-6 h-6 text-[#0096FF]" />
              </div>
              <div>
                <p className="text-3xl font-semibold text-white">
                  {messages.filter(m => m.status === 'new').length}
                </p>
                <p className="text-white/50 text-sm">New Messages</p>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-green-500/10">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-semibold text-white">
                  {messages.filter(m => m.status === 'replied').length}
                </p>
                <p className="text-white/50 text-sm">Replied</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Messages List */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-semibold text-white mb-4">Messages</h2>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#0096FF] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="bg-[#121212] border border-white/10 p-8 text-center">
                <Mail className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <p className="text-white/50">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    onClick={() => setSelectedMessage(message)}
                    className={`bg-[#121212] border p-4 cursor-pointer transition-all duration-300 ${
                      selectedMessage?.id === message.id
                        ? 'border-[#0096FF]'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-white font-medium truncate">{message.name}</p>
                      <Badge className={`text-xs px-2 py-0.5 rounded-none border ${getStatusColor(message.status)}`}>
                        {message.status}
                      </Badge>
                    </div>
                    <p className="text-white/70 text-sm truncate mb-2">{message.subject}</p>
                    <div className="flex items-center gap-2 text-white/40 text-xs">
                      <Clock className="w-3 h-3" />
                      {formatDate(message.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-white mb-4">Message Details</h2>
            {selectedMessage ? (
              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-[#0096FF]/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#0096FF]" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">{selectedMessage.name}</p>
                        <a 
                          href={`mailto:${selectedMessage.email}`}
                          className="text-[#0096FF] text-sm hover:underline"
                        >
                          {selectedMessage.email}
                        </a>
                      </div>
                    </div>
                  </div>
                  <Badge className={`px-3 py-1 rounded-none border ${getStatusColor(selectedMessage.status)}`}>
                    {selectedMessage.status}
                  </Badge>
                </div>

                <div className="mb-6">
                  <p className="text-white/50 text-sm mb-1">Subject</p>
                  <p className="text-white text-lg font-medium">{selectedMessage.subject}</p>
                </div>

                <div className="mb-6">
                  <p className="text-white/50 text-sm mb-2">Message</p>
                  <div className="bg-black/30 border border-white/10 p-4">
                    <p className="text-white/90 leading-relaxed whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 text-white/40 text-sm">
                    <Clock className="w-4 h-4" />
                    Received: {formatDate(selectedMessage.created_at)}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                      className="bg-[#0096FF] text-black hover:bg-[rgba(0,150,255,0.1)] hover:text-[#0096FF] font-medium px-4 py-2 rounded-none transition-all duration-400"
                    >
                      Reply via Email
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-[#121212] border border-white/10 p-12 text-center">
                <MessageSquare className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">Select a message to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
